import { buildCommunityPostPath, htmlFragmentToText } from "@mypxplat/xplat/core";

/**
 * One row of a "latest posts across the communities you follow" list.
 *
 * Shared by the /communities right rail and the home page section, so the two read the
 * same way — same snippet length, same relative-time wording, same avatar fallback.
 */
export interface CommunityFeedRow {
  postId: string;
  communityStringId: string;
  communityTitle: string;
  communityAccent: string | null;
  authorName: string;
  authorPhoto: string | null;
  authorInitials: string;
  snippet: string;
  agoLabel: string;
  /** null hides the line entirely, rather than rendering "0 replies". */
  replyCount: number | null;
  detailPath: string;
}

/** Title + accent by community string_id — posts carry only the id. */
export interface CommunityLookup {
  [stringId: string]: { title: string; accentColor?: string | null };
}

/** Firestore Timestamp / Date / ISO string / millis -> millis, or null when unusable. */
export function communityPostMillis(created: any): number | null {
  if (!created) return null;
  let ms: number;
  if (typeof created.toMillis === "function") ms = created.toMillis();
  else if (created instanceof Date) ms = created.getTime();
  else if (typeof created === "string") ms = new Date(created).getTime();
  else if (typeof created === "number") ms = created;
  else return null;
  return Number.isFinite(ms) ? ms : null;
}

/** "just now" / "5m" / "3h" / "2d" / "6w" / "4mo". */
export function communityFeedAgo(created: any): string {
  const ms = communityPostMillis(created);
  if (ms == null) return "";
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 8) return `${wk}w`;
  const mo = Math.floor(day / 30);
  return `${mo}mo`;
}

/** Initials for the avatar fallback: first + last word, or a single leading letter. */
export function communityFeedInitials(name: string): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Subject if there is one, else the post body's plain-text projection, capped at 140. */
export function communityFeedSnippet(post: any): string {
  const raw = (post?.subject && String(post.subject).trim()) || post?.noHtmlMessageFlat || post?.noHtmlMessage || "";
  // Rendered as text, and a subject is stored HTML-escaped ("&amp;") — see htmlFragmentToText.
  const clean = htmlFragmentToText(raw);
  return clean.length > 140 ? clean.slice(0, 137) + "…" : clean;
}

/** Projects one Firestore post into a row. */
export function buildCommunityFeedRow(post: any, communities: CommunityLookup): CommunityFeedRow {
  const stringId = post?.community_string_id || "";
  const community = communities[stringId];
  return {
    postId: post?.id,
    communityStringId: stringId,
    communityTitle: community?.title || stringId,
    communityAccent: community?.accentColor || null,
    authorName: post?.author?.name || "Unknown",
    authorPhoto: post?.author?.photo || null,
    authorInitials: communityFeedInitials(post?.author?.name || ""),
    snippet: communityFeedSnippet(post),
    agoLabel: communityFeedAgo(post?.created),
    // Parent posts carry no denormalized reply count; use whatever the caller's path
    // happened to surface.
    replyCount: typeof post?.replyCount === "number" ? post.replyCount : typeof post?.replies?.length === "number" ? post.replies.length : null,
    detailPath: buildCommunityPostPath({ postId: post?.id, communityStringId: stringId }),
  };
}
